<template>
    <Head title="Settings" />
    <div class="px-4 md:px-8" data-page="settings">
        <!-- Page header -->
        <div class="sm:flex sm:justify-between sm:items-center mb-10">
            <!-- Left: Title -->
            <div class="mb-4 sm:mb-0">
                <h1 class="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
                    Account Settings
                </h1>
            </div>
        </div>
        <!-- END PAGE HEADER -->

        <!-- TABLE -->
        <div
            class="lg:bg-white dark:bg-slate-800 lg:shadow-lg lg:rounded-sm lg:border lg:border-gray-200 dark:lg:border-slate-700 mb-8"
        >
            <div class="flex flex-col lg:flex-row">
                <div
                    class="flex flex-nowrap p-0 lg:px-5 lg:py-10 bg-white dark:bg-slate-800 shadow-lg dark:shadow-slate-900/50 rounded-sm border border-gray-200 dark:border-slate-700 mb-8 lg:border-none lg:shadow-none"
                >
                    <SettingsNav />
                </div>

                <div
                    class="p-5 lg:px-0 lg:py-0 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-gray-200 dark:border-slate-700 mb-8 lg:mb-0 lg:border-r-0 lg:border-x-0 lg:border-y-0 lg:!border-l lg:!border-gray-200 dark:lg:!border-slate-700 lg:shadow-none"
                    style="grow: 1"
                >
                    <div class="flex flex-nowrap flex-col lg:px-10 lg:py-10">
                        <div class="mb-5">
                            <h2
                                class="text-xl lg:text-2xl text-gray-800 dark:text-gray-100 font-bold mb-5"
                            >
                                My Account
                            </h2>

                            <section class="mb-8 border-b border-gray-200 dark:border-slate-700 dark:border-slate-700">
                                <form @submit.prevent="updateAccount">
                                    <h3
                                        class="text-lg lg:text-xl leading-snug text-gray-800 dark:text-gray-100 font-bold mb-1"
                                    >
                                        Your Name
                                    </h3>
                                    <div
                                        class="grid grid-cols-1 lg:grid-cols-4 gap-4 mt-5 mb-8"
                                    >
                                        <div
                                            class="col-span-1 flex items-center"
                                        >
                                            <div class="w-64 mr-4">
                                                <label
                                                    class="block text-sm font-bold mb-1"
                                                    for="name"
                                                >
                                                    Name
                                                </label>
                                                <input
                                                    id="name"
                                                    v-model="form.name"
                                                    class="form-input w-full"
                                                    placeholder="Your Name"
                                                />
                                            </div>
                                        </div>
                                        <div
                                            class="col-span-1 flex items-center"
                                        >
                                            <PhoneNumber
                                                :phone="form.phone"
                                                @updatephone="
                                                    updatePhone($event)
                                                "
                                            />
                                        </div>
                                        <div
                                            class="col-span-1 inline-flex items-center justify-start lg:justify-end lg:col-span-2"
                                        >
                                            <button
                                                type="submit"
                                                class="btn btn-primary !mt-8 lg:mt-0 !w-auto"
                                            >
                                                Save Changes
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </section>

                            <section class="mb-8 pb-8 border-b border-gray-200 dark:border-slate-700">
                                <h3
                                    class="text-lg lg:text-xl leading-snug text-gray-800 dark:text-gray-100 font-bold mb-1"
                                >
                                    Email
                                </h3>
                                <div class="text-sm dark:text-gray-300 mb-5">
                                    You can change your email, we will send you
                                    a verification to double check.
                                </div>
                                <form class="pt-2">
                                    <ChangeEmail :user-settings="userData" />
                                </form>
                            </section>

                            <section class="mb-8 pb-8 border-b border-gray-200 dark:border-slate-700">
                                <h3
                                    class="text-lg lg:text-xl leading-snug text-gray-800 dark:text-gray-100 font-bold mb-1"
                                >
                                    Password
                                </h3>
                                <div class="text-sm mb-5">
                                    You can change your password.
                                </div>
                                <form class="pt-2">
                                    <ResetPassword />
                                </form>
                            </section>

                            <section class="mb-8 pb-8 border-b border-gray-200 dark:border-slate-700">
                                <TwoFactorToggle
                                    role="list"
                                    class="mt-2 mb-8"
                                />
                            </section>

                            <section class="mb-8">
                                <PasskeySection />
                            </section>
                        </div>
                    </div>
                </div>
                <div
                    class="flex flex-nowrap p-5 lg:px-5 lg:py-10 bg-white dark:bg-slate-800 shadow-lg dark:shadow-slate-900/50 rounded-sm border border-gray-200 dark:border-slate-700 mb-8 lg:hidden"
                >
                    <ul>
                        <li class="mb-1 mr-0">
                            <h3
                                    class="text-lg lg:text-xl leading-snug text-gray-800 dark:text-gray-100 font-bold mb-1"
                            >
                                Close Account
                            </h3>
                            <Link
                                class="flex items-center -ml-2.5 px-2.5 py-2 rounded whitespace-nowrap"
                                href="/user-settings/close"
                            >
                                <svg
                                    aria-hidden="true"
                                    focusable="false"
                                    data-prefix="fas"
                                    data-icon="trash"
                                    class="w-3 h-3 shrink-0 fill-current mr-2 text-gray-400"
                                    role="img"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 448 512"
                                >
                                    <path
                                        fill="currentColor"
                                        d="M432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16zM53.2 467a48 48 0 0 0 47.9 45h245.8a48 48 0 0 0 47.9-45L416 128H32z"
                                    ></path>
                                </svg>
                                <span
                                    class="text-xs font-medium text-gray-400 hover:text-orange"
                                >
                                    Close Account
                                </span>
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { useAppStore } from '@/store/app';
import { Head, Link, useForm } from '@inertiajs/vue3';
import PhoneNumber from '@/components/phone-number.vue';
import ChangeEmail from '@/components/auth/change-email.vue';
import ResetPassword from '@/components/auth/reset-password.vue';
import TwoFactorToggle from '@/components/auth/two-factor-toggle.vue';
import PasskeySection from '@/components/auth/passkey-section.vue';
import SettingsNav from '@/Pages/Settings/partials/nav.vue';
import { notify } from 'notiwind';

const props = defineProps({
    userData: Object,
});

const appStore = useAppStore();

const form = useForm({
    name: props.userData.name,
    phone: props.userData.phone,
});

const updatePhone = (val) => {
    form.phone = appStore.phone;
};

const validatePhoneNumber = (phone) => {
    const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
    return phoneRegex.test(phone);
};

const updateAccount = () => {
    if (form.phone && !validatePhoneNumber(form.phone)) {
        notify({
            group: 'error',
            title: 'Invalid Phone Number',
            text: 'Please enter a valid phone number in the format: (###) ###-####',
            type: 'error',
        });
        return;
    }

    form.put('/user-settings');
    notify({
        group: 'success',
        title: 'Account Updated',
        text: 'Your account has been updated successfully',
        type: 'success',
    });
};
</script>
